import { useCallback } from 'react'
import {
  ChoiceList,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge
} from '@shopify/polaris'

import { useAuth } from '@/contexts/AuthContext'

interface StoreFilterProps {
  selectedStoreIds: string[]
  onChange: (storeIds: string[]) => void
  showCount?: boolean
  storeCounts?: Record<string, number>
}

export default function StoreFilter({
  selectedStoreIds,
  onChange,
  showCount = false,
  storeCounts = {}
}: StoreFilterProps) {
  const { stores, hasMultipleStores } = useAuth()

  const handleChange = useCallback((selected: string[]) => {
    onChange(selected)
  }, [onChange])

  // Don't render if only one store
  if (!hasMultipleStores) {
    return null
  }

  // Build choices with optional counts
  const choices = stores.map(store => {
    const count = storeCounts[store.id]
    const label = (
      <InlineStack gap="200" align="start" blockAlign="center">
        <Text variant="bodyMd" as="span">
          {store.name || store.shopDomain || 'Store'}
        </Text>
        {showCount && count !== undefined && (
          <Badge status="info">{count}</Badge>
        )}
      </InlineStack>
    )

    return {
      label: store.name || store.shopDomain || 'Store',
      value: store.id,
      helpText: store.whatsapp?.phoneNumber || store.platform,
      renderChildren: showCount && count !== undefined ? () => (
        <Text variant="bodySm" color="subdued" as="span">
          {count} conversation{count !== 1 ? 's' : ''}
        </Text>
      ) : undefined
    }
  })

  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingSm" as="h3">
          Filter by Account
        </Text>
        <ChoiceList
          allowMultiple
          title=""
          titleHidden
          choices={choices}
          selected={selectedStoreIds}
          onChange={handleChange}
        />
        {selectedStoreIds.length > 0 && selectedStoreIds.length < stores.length && (
          <Text variant="bodySm" color="subdued" as="p">
            Showing {selectedStoreIds.length} of {stores.length} accounts
          </Text>
        )}
      </BlockStack>
    </Card>
  )
}

// Compact version for inline use
export function StoreFilterInline({
  selectedStoreIds,
  onChange,
  storeCounts = {}
}: StoreFilterProps) {
  const { stores, hasMultipleStores } = useAuth()

  if (!hasMultipleStores) {
    return null
  }

  const handleToggle = (storeId: string) => {
    if (selectedStoreIds.includes(storeId)) {
      onChange(selectedStoreIds.filter(id => id !== storeId))
    } else {
      onChange([...selectedStoreIds, storeId])
    }
  }

  const handleSelectAll = () => {
    onChange(stores.map(s => s.id))
  }

  const handleClearAll = () => {
    onChange([])
  }

  return (
    <InlineStack gap="200" align="start" wrap>
      {stores.map(store => {
        const isSelected = selectedStoreIds.length === 0 || selectedStoreIds.includes(store.id)
        const count = storeCounts[store.id]

        return (
          <Badge
            key={store.id}
            status={isSelected ? 'info' : undefined}
            onClick={() => handleToggle(store.id)}
          >
            {store.name || store.shopDomain}
            {count !== undefined && ` (${count})`}
          </Badge>
        )
      })}
    </InlineStack>
  )
}
